// #569 — the claim of an athlete record asks for the birth date of the record.
// The server compares it: a wrong date answers 403 CLAIM_BIRTH_DATE_MISMATCH,
// and 3 wrong dates in 24 hours lock the claims of the account (429
// CLAIM_ATTEMPTS_LOCKED). A coach or the federation approves each claim.

import { useState } from 'react';
import { useI18n } from '../../i18n';
import { inlineError } from '../../hooks/useApiCall';
import { useBusy } from '../../hooks/useBusy';
import { claimAthleteRecord } from '../../api/client';
import { personName } from '../../utils/personName';
import { todayLocal } from '../../utils/calendarDate';
import ModalBackdrop from './ModalBackdrop';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import Notice from './Notice';
import Field from './Field';

interface Props {
  record: { id: string; first_name: string; last_name: string };
  /** Called after the server stored the claim. The caller reloads its lists
   *  and shows the toast. */
  onClaimed: () => void;
  onClose: () => void;
}

export default function ClaimRecordModal({ record, onClaimed, onClose }: Props) {
  const { t } = useI18n();
  const [birthDate, setBirthDate] = useState('');
  // #605, #664 — one request in flight at a time: a double click sent two POSTs (#426).
  const claim = useBusy();
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await claim.run(async () => {
      setErr('');
      try {
        await claimAthleteRecord(record.id, birthDate);
        onClaimed();
      } catch (e2) {
        setErr(inlineError(e2));
      }
    });
  }

  return (
    <ModalBackdrop onClose={onClose} maxWidth="sm" ariaLabel={t.teams.claimTitle}>
      <ModalHeader title={t.teams.claimTitle} subtitle={personName(record.first_name, record.last_name)} />
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-gray-600">{t.teams.claimBirthDateHelp}</p>
        <Field label={t.profile.birthDate} required>
          <input
            id="claim-birth-date" data-testid="claim-birth-date" data-autofocus
            type="date" className="input" max={todayLocal()}
            value={birthDate} onChange={e => setBirthDate(e.target.value)} disabled={claim.busy}
          />
        </Field>
        {err && <Notice tone="error" plain data-testid="claim-error">{err}</Notice>}
        <ModalFooter
          saveAsSubmit
          busy={claim.busy}
          saveLabel={t.teams.sendClaim}
          saveDisabled={!birthDate}
          onCancel={onClose}
        />
      </form>
    </ModalBackdrop>
  );
}
